import { DocumentFileTableActions } from '@monorepo/shared/componentsV2/documents/DocumentFileTableActions';
import { getDocumentFolderMoveDestinations } from '@monorepo/shared/utils/documentFolderScope';
import { DocumentFileResponse, DocumentFolderScope } from 'mapistry-shared';
import React, { useCallback, useMemo } from 'react';
import { useDeleteSiteDocument } from '../hooks/useDeleteSiteDocument';
import { useSiteDocumentsList } from '../hooks/useSiteDocumentsList';
import { MoveSiteDocumentModal } from './MoveSiteDocumentModal';
import { RenameSiteDocumentModal } from './RenameSiteDocumentModal';

interface SiteDocumentsTableActionsProps {
  organizationId: string;
  projectId: string;
  document: DocumentFileResponse;
  canEditSiteDocuments: boolean;
}

export function SiteDocumentsTableActions({
  organizationId,
  projectId,
  document,
  canEditSiteDocuments,
}: SiteDocumentsTableActionsProps) {
  const { siteDocuments, isLoading } = useSiteDocumentsList({
    organizationId,
    projectId,
  });
  const [deleter] = useDeleteSiteDocument({ config: { throwOnError: true } });

  const allowTopLevel = document.folderId != null;
  const moveDestinations = useMemo(
    () =>
      getDocumentFolderMoveDestinations(siteDocuments?.folders ?? [], {
        id: document.folderId ?? '',
        scope: DocumentFolderScope.PROJECT,
      }),
    [document.folderId, siteDocuments],
  );

  const onDelete = useCallback(
    () =>
      deleter({
        organizationId,
        projectId,
        documentId: document.id,
      }),
    [deleter, document.id, organizationId, projectId],
  );

  if (isLoading) return null;

  const isSiteOwnedFile = document.projectId != null;
  const showEditActions = canEditSiteDocuments && isSiteOwnedFile;

  return (
    <DocumentFileTableActions
      document={document}
      showEditActions={showEditActions}
      onDelete={onDelete}
      canMove={allowTopLevel || moveDestinations.length > 0}
      renderRenameModal={(onClose) => (
        <RenameSiteDocumentModal
          organizationId={organizationId}
          projectId={projectId}
          document={document}
          onClose={onClose}
        />
      )}
      renderMoveModal={(onClose) => (
        <MoveSiteDocumentModal
          organizationId={organizationId}
          projectId={projectId}
          document={document}
          folders={moveDestinations}
          onClose={onClose}
        />
      )}
    />
  );
}
